<template>
  <div>
    <vx-card class="mt-base" :title="activityTitle">
      <vs-row vs-align="center" vs-w="12">
        <vs-col vs-lg="12" vs-sm="12" vs-xs="12">
          <p class="mb-5">
            One of the main reasons given by those who enter jobs and careers related to
            environmental science is that they care about the environment.
          </p>
          <p class="mb-5">
            People who work in this field are often strongly motivated by a desire to protect the
            landscape and the plants and animals of the natural world from further destruction by
            the activities of man.
          </p>
          <p class="mb-5">
            Environmental stewardship refers to the responsible use and protection of the natural
            environment through conservation and sustainable practices.
          </p>
          <p class="mb-5">
            As someone who wants to work in an environmental field, litter and pollution probably
            annoy you - a lot!
          </p>
          <p class="mb-5">
            Sometimes, we need to take things into our own hands if we want to see them improve. In
            this challenge, you’ll become an environmental steward in your local community and
            identify an area to clean up.
          </p>
          <p class="mb-5">
            It’s incredible what a small group of people, or even a single individual, can achieve
            when they put their minds to it. Just take a look at some of these before and after
            photos from people who completed similar challenges.
          </p>
        </vs-col>
        <vs-col vs-lg="12" vs-sm="12" vs-xs="12">
          <img
            :src="image1"
            class="img-responsive mx-auto cursor-pointer mb-5"
            alt="Four before-and-after photo pairs of community clean-ups: a rubbish-strewn canal bank cleared back to grass, a litter-filled creek bank restored, a beach cliff covered in plastic waste cleaned up, and a rocky shoreline cleared of debris."
          />
        </vs-col>
      </vs-row>

      <p class="mb-5">
        And if you’re still not convinced of The Power of One - check out this video profile of the
        work of the UN’s Champion of the Earth, Afroz Shah.
      </p>
      <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

      <h4 class="mb-5">Your Task:</h4>

      <p class="mb-5">
        You are going to clean up an area of your choice in your local community. If possible, you
        may like to complete this challenge by working together with other friends or family
        members. However, if this isn’t possible, it can just as well be done by yourself - it might
        take a little longer, though!
      </p>
      <p class="mb-5">
        The task falls into 3 parts and you can access each of these via the tabs below. You must
        complete all 3 parts (Equipment Checklist, Hazard Identification, Cleanup).
      </p>

      <p class="mb-5">Before starting, there are a few things you need to do.</p>

      <ul class="default-list">
        <li>
          First, identify an area in your local community that could do with some tender, loving
          care. It could be a riverbank, a neglected park, a copse of trees, anywhere in your local
          community where litter has piled up. If you don’t have a place in mind, go for a walk to
          scout out a location.
        </li>
        <li>
          Now, take your ‘Before’ photos. Try to cover the area from several angles to give a
          realistic view of what it looks like. As well as taking some close-ups, take a few photos
          from a distance that put your clean-up site in context.
        </li>
      </ul>

      <p class="mb-5">
        Now work through each of the steps in the 3 tabs below to complete this challenge/activity:
      </p>
    </vx-card>
    <vs-tabs alignment="fixed" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Equipment Checklist">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Equipment Checklist:</h4>
            <p class="mb-5">
              First, you’ll need to gather up the necessary equipment to complete your task. Use the
              checklist below.
            </p>

            <table class="table default-table">
              <tr class="header">
                <th scope="col" colspan="2">To Complete This Task, You Will Need:</th>
                <th scope="col">✔</th>
              </tr>
              <tr class="body" v-for="(item, index) in equipment_checklist" :key="index">
                <th scope="row" width="5%" class="subheader">{{ index + 1 }}.</th>
                <td width="90%">{{ item }}</td>
                <td width="9%">
                  <vs-checkbox v-model="data['question1_' + index]">{{ item.name }}</vs-checkbox>
                </td>
              </tr>
            </table>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Hazard Identification">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Hazard Identification</h4>
            <p class="mb-5">
              Once you’ve identified your area, you’ll need to do a basic risk assessment to ensure
              your safety and the safety of any other participants.
            </p>
            <p class="mb-5">
              Examine it closely for safety hazards. For example, perhaps it’s near a river, and
              there is a risk of falling in.
            </p>
            <p class="mb-5">
              Or, maybe there is broken glass or other sharp objects. Is the ground slippery? Are
              there dogs in the area?
            </p>
            <p class="mb-5">
              Brainstorm the potential hazards and make a list below, as well as any solutions you
              might have, e.g., take litter pickers to avoid sharp objects, wear extra thick gloves,
              hiking boots, thick clothes, carry a stick, etc.
            </p>
            <p class="mb-5">When you’re ready, complete the table below.</p>

            <table class="table default-table">
              <tr class="header">
                <th scope="col" width="50%">Hazard</th>
                <th scope="col">Precautions</th>
              </tr>
              <tr class="body" v-for="index in 8" :key="index">
                <td class="smallpadding">
                  <text-block v-model="data['question2_' + index + '_1']" :editable="isEditable" />
                </td>
                <td class="smallpadding">
                  <text-block v-model="data['question2_' + index + '_2']" :editable="isEditable" />
                </td>
              </tr>
            </table>
            <p class="mb-5">
              You’ll have to use your judgement here. You are responsible for ensuring your own
              safety while completing this activity (and the safety of other participants). If the
              area is too dangerous (e.g. discarded needles onsite is a BIG no-no!), look for a more
              suitable area for your clean-up.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Cleanup">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">Cleanup</h4>

            <p class="mb-5">
              When you have done your due diligence and taken all the necessary precautions, it’s
              time to start the clean-up. Approach the job methodically, cleaning the area section
              by section. Where possible, sort the rubbish as you go into what is recyclable and
              what isn’t. Remember to take plenty of photographs as you go too.
            </p>
            <p class="mb-5">
              When you have bagged all the rubbish in your chosen area, it’s time to transport it to
              your local dump and/or your local recycling centre. You’ll need transport for that, so
              if you aren’t driving yet, ask a relative or friend who is able to help.
            </p>
            <p class="mb-5">
              Finally, take some pictures of the cleaned-up site. Try to take them from the same
              perspectives as you took your original photos of the site when it was a mess.
            </p>
            <p class="mb-5">
              Now, go to your gallery of photographs and choose one before and one after photo from
              the same perspective to best represent your project.
            </p>

            <p class="mb-5"><strong>My Best “Before” photo:</strong></p>
            <fileupload-button-immediate
              v-model="uploaded_files['uploaded_file_1']"
              :is-editable="isEditable"
              filetype="photo"
              index="1"
            />

            <p class="mb-5"><strong>My Best “After” photo:</strong></p>
            <fileupload-button-immediate
              v-model="uploaded_files['uploaded_file_2']"
              :is-editable="isEditable"
              filetype="photo"
              index="2"
            />
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <save-buttons :is-editable="isEditable" @save="save" />
  </div>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/envscience/transform_polluted_beach_splash.jpg'),
          video: 'https://www.youtube.com/embed/jXcoPO76UyA',
        },
      },
      image1: require('@/assets/images/experiences/envscience/activity_2_image.jpg'),
      equipment_checklist: [
        'Work Gloves',
        'Rubbish Bags',
        'Litter Picker',
        'Camera (your smartphone will do)',
        'Shovel',
        'Bucket',
        'Car (or friend/family member with a car)',
      ],
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
